import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs } from '@carboledger/ui';
import { ROUTES } from '@/app/routes';
import { useTranslations } from '@/hooks';
import {
  buildInventoryFilters,
  CONTRACT_FILTER_KEYS,
  SHIPMENT_FILTER_KEYS,
  DECLARATION_FILTER_KEYS,
} from '@/lib/mappers';
import { EMPTY_MAP } from '@/lib/utils';
import { ContractsTable } from '@/slices/contracts';
import { IncomingShipmentsTable } from '@/slices/incoming-shipments';
import { DeclarationsTable } from '@/slices/declarations';
import { INVENTORY_INBOUND_TABS } from '../constants/inventory.constants';
import {
  buildContractTiles,
  buildShipmentTiles,
  buildDeclarationTiles,
} from '../constants/inventoryTiles';
import {
  useGetContractsStripQuery,
  useGetShipmentsStripQuery,
  useGetDeclarationsStripQuery,
} from '../api/inventoryApi';
import { InventoryListTab } from './InventoryListTab';
import { buildTabEmptyState, buildTabNoResults } from '../constants/inventoryEmptyStates';
import { RECEIVED_DECLARATION_STATUSES } from '@/config/statuses';

/**
 * Props:
 *   module           the active module route segment
 *   certificateType  the module's `CertificateType`
 *   item         the inventory row's facility, material and terminal
 */
export const InventoryInboundsTabView = ({ module, certificateType, item }) => {
  const t = useTranslations();
  const navigate = useNavigate();
  const [activeSubTab, setActiveSubTab] = useState(INVENTORY_INBOUND_TABS.contracts);

  const subTabs = useMemo(
    () => [
      { value: INVENTORY_INBOUND_TABS.contracts, label: t('NAV_CONTRACTS') },
      { value: INVENTORY_INBOUND_TABS.incomingShipments, label: t('NAV_INCOMING_SHIPMENTS') },
      { value: INVENTORY_INBOUND_TABS.declarations, label: t('SECTION_DECLARATIONS') },
    ],
    [t],
  );

  const header = (
    <Tabs
      tabs={subTabs}
      value={activeSubTab}
      onChange={setActiveSubTab}
      aria-label={t('NAV_INBOUNDS')}
    />
  );

  const contractFilters = useMemo(() => buildInventoryFilters(item, CONTRACT_FILTER_KEYS), [item]);
  const shipmentFilters = useMemo(() => buildInventoryFilters(item, SHIPMENT_FILTER_KEYS), [item]);
  const declarationFilters = useMemo(
    () => buildInventoryFilters(item, DECLARATION_FILTER_KEYS),
    [item],
  );

  const declarationStripArgs = useMemo(() => ({ certificateType }), [certificateType]);
  // Totals of received declarations only: the server blanks a total if any row in it has no figure yet.
  const declarationStripFilters = useMemo(
    () => declarationFilters && { ...declarationFilters, status: RECEIVED_DECLARATION_STATUSES },
    [declarationFilters],
  );

  const addContract = useCallback(
    () => navigate(ROUTES.contractCreate(module)),
    [navigate, module],
  );
  const addShipment = useCallback(
    () => navigate(ROUTES.incomingShipmentCreate(module)),
    [navigate, module],
  );
  const addDeclaration = useCallback(
    () => navigate(ROUTES.declarationCreate(module)),
    [navigate, module],
  );

  const contractsEmpty = useMemo(
    () =>
      buildTabEmptyState(t, {
        titleKey: 'CONTRACTS_EMPTY_TITLE',
        descriptionKey: 'CONTRACTS_EMPTY_DESCRIPTION',
        addKey: 'CONTRACTS_ADD',
        onAdd: addContract,
      }),
    [t, addContract],
  );
  const shipmentsEmpty = useMemo(
    () =>
      buildTabEmptyState(t, {
        titleKey: 'INCOMING_SHIPMENTS_EMPTY_TITLE',
        descriptionKey: 'INCOMING_SHIPMENTS_EMPTY_DESCRIPTION',
        addKey: 'INCOMING_SHIPMENTS_ADD',
        onAdd: addShipment,
      }),
    [t, addShipment],
  );
  const declarationsEmpty = useMemo(
    () =>
      buildTabEmptyState(t, {
        titleKey: 'DECLARATIONS_LIST_EMPTY_TITLE',
        descriptionKey: 'DECLARATIONS_LIST_EMPTY_DESCRIPTION',
        addKey: 'ACTION_ADD_DECLARATIONS',
        onAdd: addDeclaration,
      }),
    [t, addDeclaration],
  );

  const contractsNoResults = useMemo(() => buildTabNoResults(t, 'CONTRACTS_NO_RESULTS_TITLE'), [t]);
  const shipmentsNoResults = useMemo(
    () => buildTabNoResults(t, 'INCOMING_SHIPMENTS_NO_RESULTS_TITLE'),
    [t],
  );
  const declarationsNoResults = useMemo(
    () => buildTabNoResults(t, 'DECLARATIONS_NO_RESULTS_TITLE'),
    [t],
  );

  const openContract = useCallback(
    (row) => navigate(ROUTES.contractDetail(module, row.id)),
    [navigate, module],
  );
  const openShipment = useCallback(
    (row) => navigate(ROUTES.incomingShipmentDetail(module, row.id)),
    [navigate, module],
  );
  const openDeclaration = useCallback(
    (row) => navigate(ROUTES.declarationDetail(module, row.id)),
    [navigate, module],
  );

  if (activeSubTab === INVENTORY_INBOUND_TABS.contracts) {
    return (
      <InventoryListTab
        key={activeSubTab}
        header={header}
        filters={contractFilters}
        useStrip={useGetContractsStripQuery}
        stripArgs={EMPTY_MAP}
        buildTiles={buildContractTiles}
      >
        {(onEmptyChange) => (
          <ContractsTable
            extraFilters={contractFilters}
            emptyState={contractsEmpty}
            noResults={contractsNoResults}
            onEmptyChange={onEmptyChange}
            onOpenContract={openContract}
          />
        )}
      </InventoryListTab>
    );
  }

  if (activeSubTab === INVENTORY_INBOUND_TABS.incomingShipments) {
    return (
      <InventoryListTab
        key={activeSubTab}
        header={header}
        filters={shipmentFilters}
        useStrip={useGetShipmentsStripQuery}
        stripArgs={EMPTY_MAP}
        buildTiles={buildShipmentTiles}
      >
        {(onEmptyChange) => (
          <IncomingShipmentsTable
            extraFilters={shipmentFilters}
            emptyState={shipmentsEmpty}
            noResults={shipmentsNoResults}
            onEmptyChange={onEmptyChange}
            onOpenShipment={openShipment}
          />
        )}
      </InventoryListTab>
    );
  }

  return (
    <InventoryListTab
      key={activeSubTab}
      header={header}
      filters={declarationFilters}
      stripFilters={declarationStripFilters}
      useStrip={useGetDeclarationsStripQuery}
      stripArgs={declarationStripArgs}
      buildTiles={buildDeclarationTiles}
    >
      {(onEmptyChange) => (
        <DeclarationsTable
          certificateType={certificateType}
          extraFilters={declarationFilters}
          emptyState={declarationsEmpty}
          noResults={declarationsNoResults}
          onEmptyChange={onEmptyChange}
          onView={openDeclaration}
        />
      )}
    </InventoryListTab>
  );
};
